import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import AppShell from './components/AppShell';

// Shell + redirect stay eager — small and needed for first CRM navigation.
import CrmLayout from './pages/crm/CrmLayout';
import CrmIndexRedirect from './pages/crm/CrmIndexRedirect';

const LandingPage = lazy(() => import('./pages/Landing'));
const LoginPage = lazy(() => import('./pages/Login'));
const SignupPage = lazy(() => import('./pages/Signup'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallback'));
const OnboardingPage = lazy(() => import('./pages/onboarding/Onboarding'));
const DashboardPage = lazy(() => import('./pages/Dashboard'));

const OverviewPage = lazy(() => import('./pages/crm/Overview'));
const PipelinePage = lazy(() => import('./pages/crm/Pipeline'));
const CustomersPage = lazy(() => import('./pages/crm/Customers'));
const CustomerDetailPage = lazy(() => import('./pages/crm/CustomerDetail'));
const JobDetailPage = lazy(() => import('./pages/crm/JobDetail'));
const ActivityPage = lazy(() => import('./pages/crm/Activity'));
const AnalyticsPage = lazy(() => import('./pages/crm/Analytics'));
const InboxPage = lazy(() => import('./pages/crm/Inbox'));
const WorkflowsPage = lazy(() => import('./pages/crm/Workflows'));
const DocumentsPage = lazy(() => import('./pages/crm/Documents'));
const GridPage = lazy(() => import('./pages/crm/Grid'));
const SignPage = lazy(() => import('./pages/public/Sign'));
const PrivacyPage = lazy(() => import('./pages/public/Privacy'));
const TermsPage = lazy(() => import('./pages/public/Terms'));
const SmsOptInPage = lazy(() => import('./pages/public/SmsOptIn'));
const GoogleSignInPage = lazy(() => import('./pages/public/GoogleSignIn'));
const ContactPage = lazy(() => import('./pages/public/Contact'));
const PricingPage = lazy(() => import('./pages/public/Pricing'));
const TechPortalPage = lazy(() => import('./pages/public/TechPortal'));
const SettingsPage = lazy(() => import('./pages/crm/Settings'));
const PayrollPage = lazy(() => import('./pages/crm/Payroll'));
const ReportsPage = lazy(() => import('./pages/crm/Reports'));
const OrdersPage = lazy(() => import('./pages/crm/Orders'));
import { AdminProtected } from './pages/admin/AdminLayout';

const TeamPage = lazy(() => import('./pages/crm/Team'));

const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminOverviewPage = lazy(() => import('./pages/admin/Overview'));
const AdminUsersPage = lazy(() => import('./pages/admin/Users'));
const AdminUserDetailPage = lazy(() => import('./pages/admin/UserDetail'));
const AdminAuditPage = lazy(() => import('./pages/admin/Audit'));

function RouteFallback() {
  return (
    <div className="grid min-h-[10rem] place-items-center p-8 text-sm text-zinc-500" role="status">
      Loading…
    </div>
  );
}

function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="grid h-screen place-items-center text-zinc-500 text-sm">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

export default function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/"               element={<LandingPage />} />
        <Route path="/login"          element={<LoginPage />} />
        <Route path="/signup"         element={<SignupPage />} />
        <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
        <Route path="/reset-password"  element={<Navigate to="/login" replace />} />
        <Route path="/auth/callback"   element={<AuthCallbackPage />} />

        <Route path="/book/:slug"     element={<Navigate to="/" replace />} />
        <Route path="/sign/:token"    element={<SignPage />} />
        <Route path="/tech/:token"    element={<TechPortalPage />} />
        <Route path="/privacy"        element={<PrivacyPage />} />
        <Route path="/terms"          element={<TermsPage />} />
        <Route path="/sms-opt-in"     element={<SmsOptInPage />} />
        <Route path="/google-sign-in" element={<GoogleSignInPage />} />
        <Route path="/contact"        element={<ContactPage />} />
        <Route path="/pricing"        element={<PricingPage />} />

        <Route path="/onboarding"      element={<Protected><OnboardingPage /></Protected>} />
        <Route path="/onboarding/:id"  element={<Protected><OnboardingPage /></Protected>} />
        <Route path="/dashboard/:id"   element={<Protected><DashboardPage /></Protected>} />
        <Route path="/dashboard"       element={<Protected><DashboardPage /></Protected>} />

        <Route path="/crm" element={<Protected><CrmIndexRedirect /></Protected>} />

        <Route path="/crm/:businessId" element={<Protected><CrmLayout /></Protected>}>
          <Route index                              element={<Navigate to="overview" replace />} />
          <Route path="overview"                    element={<OverviewPage />} />
          <Route path="pipeline"                    element={<PipelinePage />} />
          <Route path="customers"                   element={<CustomersPage />} />
          <Route path="customers/:customerId"       element={<CustomerDetailPage />} />
          <Route path="jobs/:jobId"                 element={<JobDetailPage />} />
          <Route path="activity"                    element={<ActivityPage />} />
          <Route path="analytics"                   element={<AnalyticsPage />} />
          <Route path="inbox"                       element={<InboxPage />} />
          <Route path="inbox/:customerId"           element={<InboxPage />} />
          <Route path="workflows"                   element={<WorkflowsPage />} />
          <Route path="bookings"                    element={<Navigate to="../pipeline" replace />} />
          <Route path="documents"                   element={<DocumentsPage />} />
          <Route path="grid"                        element={<GridPage />} />
          <Route path="reports"                     element={<ReportsPage />} />
          <Route path="payroll"                     element={<PayrollPage />} />
          <Route path="team"                        element={<TeamPage />} />
          <Route path="orders"                       element={<OrdersPage />} />
          <Route path="insights"                    element={<Navigate to="../analytics" replace />} />
          <Route path="settings"                    element={<SettingsPage />} />
          <Route path="settings/:section"           element={<SettingsPage />} />
        </Route>

        <Route path="/admin" element={<Protected><AdminProtected><AdminLayout /></AdminProtected></Protected>}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:userId" element={<AdminUserDetailPage />} />
          <Route path="audit" element={<AdminAuditPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
